import type { KeyNameType } from '@tunasong/music-lib'
import { keyDisplayName, spokenKeyName } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { Drawer, Popover, useTunaMediaQuery } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { KeySelect } from './key-select.js'
import styles from './key-button.module.css'

export interface KeyButtonProps {
  className?: string
  value?: Partial<Scale> | null
  variant?: KeyNameType
  allowNone?: boolean
  onChange(scale: Scale | null): void
}

/** A key as the chip reads it; a partial key (no root or type yet) reads as no key */
const asKey = (value: Partial<Scale> | null | undefined) =>
  value?.root && value.type ? { root: value.root, type: value.type } : null

/**
 * The key as a setting above a tool page's content (one key picker everywhere, #3004; spec
 * docs/ux/2026-10-31-key-picker-everywhere.md §2, §4, §7): a quiet "Key" label and a one-line chip, "B♭ major ⌄",
 * that opens the key control, `KeySelect` unchanged, in a popover, or a bottom drawer on a phone. The song key chip's
 * pattern. Picks apply at once and the picker stays open, as the arrow keys select as they move; Esc, a click outside
 * or Done close it, and focus goes back to the chip. Opening, focus goes to the selected root.
 */
export const KeyButton: FC<KeyButtonProps> = props => {
  const { className, value = null, variant = 'common', allowNone = true, onChange } = props
  const isPhone = useTunaMediaQuery('(max-width:599.95px)')
  const chipRef = useRef<HTMLButtonElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  /**
   * Opening, focus goes to the grid's tab stop: the selected root, or the first with no key (spec §4). The surface
   * decides focus after it opens, so it is told what to focus rather than having it focused before
   */
  const selectedRoot = () => pickerRef.current?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]') ?? null

  const handleClose = () => {
    setOpen(false)
    chipRef.current?.focus()
  }

  const key = asKey(value)
  const picker = open ? (
    <div ref={pickerRef} className={styles.picker}>
      {isPhone ? (
        <div className={styles.header}>
          <h2 className={styles.title}>Key</h2>
          <button type="button" className={styles.done} onClick={handleClose}>
            Done
          </button>
        </div>
      ) : null}
      <KeySelect
        value={value}
        variant={variant}
        allowNone={allowNone}
        moreAs={isPhone ? 'view' : 'menu'}
        moreBackLabel="Key"
        onChange={onChange}
      />
    </div>
  ) : null

  return (
    <div className={cn(styles.row, className)}>
      <span className={styles.label}>Key</span>
      <button
        ref={chipRef}
        type="button"
        className={styles.chip}
        aria-label={key ? `Key: ${spokenKeyName(key)}` : 'Key: no key'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {key ? keyDisplayName(key) : 'No key'} ⌄
      </button>
      {isPhone ? (
        <Drawer
          open={open}
          onClose={handleClose}
          anchor="bottom"
          width="auto"
          aria-label="Key"
          initialFocus={selectedRoot}
        >
          {picker}
        </Drawer>
      ) : (
        <Popover
          open={open}
          onClose={handleClose}
          anchorEl={chipRef.current}
          aria-label="Key"
          initialFocus={selectedRoot}
        >
          {picker}
        </Popover>
      )}
    </div>
  )
}
